import { Component, createRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import ShelfLifeBadge from "../dbadmin/ShelfLifeBadge";
import IssueModal from "../donormanagement/IssueModal";

class BloodDonations extends Component {
  constructor(props) {
    super(props);
    this.state = {
      donations: [],
      donorsList: [],
      loading: true,
      saving: false,
      error: "",
      success: "",

      // Hidden scan input buffer
      scanBuffer: "",

      // Last scanned preview
      scanned: null,

      // Scan mode + active/paused
      scanMode: "add",
      scanActive: true,

      // Manual entry form
      showManual: false,
      manualDonorId: "",
      manualBGroup: "",
      manualDate: new Date().toISOString().split("T")[0],
      manualCamp: "",
      manualPatientId: "",
      manualDonorName: "", // Added for free-text donor name

      // Edit mode
      isEditing: false,
      editId: null,

      // Delete confirm
      showDeleteConfirm: false,
      deleteTarget: null,
      deleting: false,

      // Search / filter
      searchTerm: "",
      filterBloodGroup: "",

      // Discard confirm
      discardTarget: null,
      discarding: false,

      viewMode: "active",
      statusFilter: "",

      // Per-row Issue modal
      issueTarget: null,
    };

    this.scanInputRef = createRef();
  }

  componentDidMount() {
    this.fetchDonations();
    this.fetchDonorsList();
    this.focusScanInput();
  }

  focusScanInput() {
    if (this.scanInputRef.current) {
      this.scanInputRef.current.focus();
    }
  }

  showAlert(type, msg) {
    this.setState({ [type]: msg });
    setTimeout(() => this.setState({ [type]: "" }), 4000);
  }

  // ── Active donations ──
  getActiveDonations() {
    return this.state.donations.filter(
      (d) => !["discarded", "issued"].includes(d.status),
    );
  }

  // ── Fetch all donations ──
  async fetchDonations() {
    this.setState({ loading: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blood-donations`);
      const data = await res.json();
      this.setState({ donations: data.data || [], loading: false });
    } catch {
      this.setState({ loading: false });
      this.showAlert("error", "Failed to load donations.");
    }
  }

getIneligibleDonorIds(excludeDonationId = null) {
  const { donations, donorsList } = this.state;
  const today = new Date();

  const intervalDays = (gender) => {
    const g = (gender || "").trim().toLowerCase();
    if (g === "male") return 90;
    if (g === "female") return 120;
    return null;
  };

  const ineligible = new Set();

  donorsList.forEach((donor) => {
    const donorPk = String(donor.id);
    const required = intervalDays(donor.gender);
    if (required === null) return;

    const donorDonations = donations.filter(
      (d) =>
        String(d.donor_id) === donorPk &&
        d.status !== "discarded" &&
        String(d.id) !== String(excludeDonationId), // exclude the record being edited
    );

    if (donorDonations.length === 0) return;

    const lastDonation = donorDonations.sort(
      (a, b) => new Date(b.donation_date) - new Date(a.donation_date),
    )[0];

    const daysSince = Math.floor(
      (today - new Date(lastDonation.donation_date)) / (1000 * 60 * 60 * 24),
    );

    if (daysSince < required) {
      ineligible.add(donorPk);
    }
  });

  return ineligible;
}

  async fetchDonorsList() {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/donors`, { credentials: "include" });
      const data = await res.json();
      this.setState({ donorsList: data.data || data.donors || [] });
    } catch {
      // silent
    }
  }

  // ── Barcode scan handler ──
  handleScanKeyDown(e) {
    if (e.key === "Enter") {
      const raw = this.state.scanBuffer.trim();
      this.setState({ scanBuffer: "" });
      if (!raw) return;

      const parts = raw.split(/[|~*]/);
      if (parts.length < 4) {
        return this.showAlert(
          "error",
          `Invalid barcode format. Got: "${raw}". Expected: ID|PatientId|BloodGroup|Date`,
        );
      }

      const donor_id = parts[0].trim();
      const patient_id = parts[1].trim();
      const blood_group = parts[2].trim().toUpperCase();
      const donation_date = parts[3].trim();

      if (this.state.scanMode === "issue") {
        return this.handleIssueScan({
          donor_id,
          patient_id,
          blood_group,
          donation_date,
          raw,
        });
      }

      this.setState({
        scanned: {
          mode: "add",
          donor_id,
          patient_id,
          blood_group,
          donation_date,
          raw,
        },
      });
      this.saveScan({ donor_id, patient_id, blood_group, donation_date, raw });
    }
  }

  // ── Save scanned / manual donation ──
  async saveScan({
    donor_id,
    patient_id,
    blood_group,
    donation_date,
    raw,
    camp_name,
    donor_name,
    donor_phone,
  }) {
    this.setState({ saving: true });
    try {
      const body = {
        raw: raw || null,
        donor_id: donor_id || null,
        patient_id: patient_id || null,
        blood_group,
        donation_date,
        camp_name: camp_name || null,
      };

      // If editing, use PATCH instead of POST
      const url = this.state.isEditing && this.state.editId
        ? `${process.env.REACT_APP_API_BASE_URL}/api/blood-donations/${this.state.editId}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/blood-donations/scan`;
      
      const method = this.state.isEditing && this.state.editId ? "PATCH" : "POST";

      // Add donor_name and donor_phone for manual entry
      if (donor_name) body.donor_name = donor_name;
      if (donor_phone) body.donor_phone = donor_phone;

      const res = await fetch(url, {
        method: method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (res.ok) {
        this.showAlert(
          "success",
          data.message || (this.state.isEditing ? "Donation updated successfully." : "Donation saved successfully."),
        );
        this.fetchDonations();
        this.setState({
          isEditing: false,
          editId: null,
          showManual: false,
        });
        setTimeout(() => this.setState({ scanned: null }), 3000);
      } else {
        this.showAlert("error", data.message || "Failed to save.");
        this.setState({ scanned: null });
      }
    } catch {
      this.showAlert("error", "Network error. Could not save donation.");
      this.setState({ scanned: null });
    } finally {
      this.setState({ saving: false });
      this.focusScanInput();
    }
  }

  // ── Open Manual Modal for Edit ──
openEditModal(donation) {
  const donor = this.state.donorsList.find(
    (d) =>
      String(d.id) === String(donation.donor_id) ||
      String(d.donor_id) === String(donation.donor_id),
  );

  this.setState({
    showManual: true,
    isEditing: true,
    editId: donation.id,
    manualDonorId: donor ? String(donor.donor_id || donor.id) : String(donation.donor_id),
    manualBGroup: donation.blood_group,
    manualDate: donation.donation_date,
    manualPatientId: donation.patient_id || "",
    scanActive: false,
  });
}

  // ── Manual form submit ──
  handleManualSubmit(e) {
    e.preventDefault();
    const {
      manualDonorId,
      manualBGroup,
      manualDate,
      manualCamp,
      manualPatientId,
      manualDonorName,
      isEditing,
    } = this.state;

    if (!manualBGroup) {
      return this.showAlert("error", "Blood group is required.");
    }
    if (!manualDate) {
      return this.showAlert("error", "Donation date is required.");
    }

    // If editing, we can update donor_name too
    const payload = {
      donor_id: manualDonorId.trim(),
      patient_id: manualPatientId.trim(),
      blood_group: manualBGroup,
      donation_date: manualDate,
      camp_name: manualCamp,
      raw: null,
    };

    // If donor name is provided (for manual entry), include it
    if (manualDonorName && manualDonorName.trim()) {
      payload.donor_name = manualDonorName.trim();
    }

    this.saveScan(payload);

    // Reset form but keep editing state until save completes
    // The saveScan will reset isEditing after success
  }

  // ── Resolve a scanned bag ──
  findMatchingDonation({ donor_id, blood_group, donation_date, raw }) {
    const { donations } = this.state;
    const bySerial = donations.find((d) => d.bag_serial === raw);
    if (bySerial) return bySerial;

    return donations.find(
      (d) =>
        String(d.donor_code ?? d.donor_id) === String(donor_id) &&
        d.blood_group === blood_group &&
        d.donation_date === donation_date,
    );
  }

  // ── Issue mode: scan ──
  async handleIssueScan({
    donor_id,
    patient_id,
    blood_group,
    donation_date,
    raw,
  }) {
    const match = this.findMatchingDonation({
      donor_id,
      blood_group,
      donation_date,
      raw,
    });

    if (!match) {
      return this.showAlert(
        "error",
        `No matching bag found for scan: "${raw}".`,
      );
    }
    if (["discarded", "issued"].includes(match.status)) {
      return this.showAlert(
        "error",
        `This bag is already ${match.status} and can't be issued.`,
      );
    }

    this.setState({
      saving: true,
      scanned: {
        mode: "issue",
        donor_id,
        patient_id,
        blood_group,
        donation_date,
        raw,
      },
    });

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/blood-issues`,
        {
          credentials: "include",
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            donation_id: match.id,
            patient_id: patient_id || match.patient_id || null,
            issue_date: new Date().toISOString().split("T")[0],
          }),
        },
      );
      const data = await res.json();

      if (res.ok) {
        this.showAlert(
          "success",
          data.message || "Blood bag issued successfully.",
        );
        this.fetchDonations();
        setTimeout(() => this.setState({ scanned: null }), 3000);
      } else {
        this.showAlert("error", data.message || "Failed to issue bag.");
        this.setState({ scanned: null });
      }
    } catch (err) {
      console.error("handleIssueScan failed:", err);
      this.showAlert("error", "Network error. Could not issue bag.");
      this.setState({ scanned: null });
    } finally {
      this.setState({ saving: false });
      this.focusScanInput();
    }
  }

  // ── Discard ──
  async handleDiscard(id) {
    this.setState({ discarding: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blood-donations/${id}/discard`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        this.setState({ discardTarget: null });
        this.showAlert("success", data.message || "Bag marked as discarded.");
        this.fetchDonations();
      } else {
        this.showAlert("error", data.message || "Failed to discard bag.");
      }
    } catch {
      this.showAlert("error", "Network error.");
    } finally {
      this.setState({ discarding: false });
    }
  }

  // ── Delete with confirmation ──
  openDeleteConfirm(donation) {
    this.setState({
      showDeleteConfirm: true,
      deleteTarget: donation,
      scanActive: false,
    });
  }

  async handleDelete() {
    const { deleteTarget } = this.state;
    if (!deleteTarget) return;

    this.setState({ deleting: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blood-donations/${deleteTarget.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        this.setState({ 
          showDeleteConfirm: false, 
          deleteTarget: null,
          scanActive: true,
        });
        this.showAlert("success", "Record deleted successfully.");
        this.fetchDonations();
        this.focusScanInput();
      } else {
        this.showAlert("error", data.message || "Delete failed.");
      }
    } catch {
      this.showAlert("error", "Network error.");
    } finally {
      this.setState({ deleting: false });
    }
  }

  // ── Filtered donations ──
  getFiltered() {
    const { donations, searchTerm, filterBloodGroup, statusFilter, viewMode } =
      this.state;

    const base = donations.filter((d) =>
      viewMode === "discarded"
        ? d.status === "discarded"
        : !["discarded", "issued"].includes(d.status),
    );

    return base.filter((d) => {
      const matchSearch =
        !searchTerm ||
        d.donor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(d.donor_id).includes(searchTerm) ||
        String(d.patient_id ?? "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const matchGroup =
        !filterBloodGroup || d.blood_group === filterBloodGroup;
      const matchStatus =
        viewMode === "discarded" || !statusFilter || d.status === statusFilter;
      return matchSearch && matchGroup && matchStatus;
    });
  }

  // ── Blood group badge color ──
  bgColor(group) {
    const map = {
      "A+": "#e74c3c",
      "A-": "#c0392b",
      "B+": "#2980b9",
      "B-": "#1a5276",
      "AB+": "#8e44ad",
      "AB-": "#6c3483",
      "O+": "#27ae60",
      "O-": "#1e8449",
    };
    return map[group] || "#555";
  }

  getDonorById(donorId) {
    return this.state.donorsList.find(
      (d) =>
        String(d.donor_id) === String(donorId) ||
        String(d.id) === String(donorId),
    );
  }

  formatDate(date) {
    if (!date) return "—";
    const d = new Date(date);
    return d
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      })
      .replace(/ /g, "-");
  }

  render() {
    const {
      loading,
      saving,
      error,
      success,
      scanBuffer,
      scanned,
      scanMode,
      scanActive,
      showManual,
      manualDonorId,
      manualBGroup,
      manualDate,
      manualCamp,
      manualPatientId,
      manualDonorName,
      isEditing,
      deleting,
      searchTerm,
      filterBloodGroup,
      showDeleteConfirm,
      deleteTarget,
    } = this.state;
    const ineligibleDonorIds = this.getIneligibleDonorIds(
  this.state.isEditing ? this.state.editId : null,
);
    const filtered = this.getFiltered();
    const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

    const activeDonations = this.getActiveDonations();
    const total = activeDonations.length;
    const todayStr = new Date().toISOString().split("T")[0];
    const todayCount = activeDonations.filter((d) => {
      if (!d.created_at) return false;
      const createdDateStr = new Date(d.created_at).toISOString().split("T")[0];
      return createdDateStr === todayStr;
    }).length;
    const groupCounts = bloodGroups.reduce((acc, g) => {
      acc[g] = activeDonations.filter((d) => d.blood_group === g).length;
      return acc;
    }, {});

    return (
      <div
        style={{ background: "#f4f6fb",  }}
        className="pt-5 mt-4"
      >
        {/* Hidden barcode scan input */}
        <input
          ref={this.scanInputRef}
          type="text"
          value={scanBuffer}
          onChange={(e) => this.setState({ scanBuffer: e.target.value })}
          onKeyDown={(e) => this.handleScanKeyDown(e)}
          onBlur={() => {
            if (scanActive) this.focusScanInput();
          }}
          style={{
            position: "fixed",
            top: -9999,
            left: -9999,
            opacity: 0,
            width: 1,
            height: 1,
          }}
          tabIndex={-1}
          readOnly={!scanActive}
          aria-hidden="true"
        />

        <div className="container py-4" >
          {/* Page Header */}
          <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
            <div>
              <h4 className="fw-bold mb-1" style={{ color: "#c9140e" }}>
                <i className="bi bi-droplet-fill me-2" />
                Blood Camp Donations
              </h4>
              <p className="text-muted small mb-0">
                Choose a scan mode, then scan a blood bag barcode — or use
                Manual Entry.
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button
                onClick={() =>
                  this.setState({ scanMode: "add", scanActive: true }, () =>
                    this.focusScanInput(),
                  )
                }
                style={{
                  background:
                    scanMode === "add" && scanActive ? "#d1e7dd" : "#fff",
                  color: scanMode === "add" && scanActive ? "#0f5132" : "#555",
                  border: `1.5px solid ${scanMode === "add" && scanActive ? "#198754" : "#ccc"}`,
                  borderRadius: 8,
                  padding: "9px 16px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <i className="bi bi-upc-scan" />
                {scanMode === "add" && scanActive
                  ? "Scanning..."
                  : "Scan to Add"}
              </button>

              <button
                onClick={() =>
                  this.setState({ scanMode: "issue", scanActive: true }, () =>
                    this.focusScanInput(),
                  )
                }
                style={{
                  background:
                    scanMode === "issue" && scanActive ? "#fff3cd" : "#fff",
                  color:
                    scanMode === "issue" && scanActive ? "#856404" : "#555",
                  border: `1.5px solid ${scanMode === "issue" && scanActive ? "#ffc107" : "#ccc"}`,
                  borderRadius: 8,
                  padding: "9px 16px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <i className="bi bi-box-arrow-right" />
                {scanMode === "issue" && scanActive
                  ? "Scanning to Issue..."
                  : "Scan to Issue"}
              </button>

              <button
                onClick={() => {
                  this.setState(
                    (s) => ({ scanActive: !s.scanActive }),
                    () => {
                      if (this.state.scanActive) this.focusScanInput();
                    },
                  );
                }}
                title={scanActive ? "Pause scanning" : "Resume scanning"}
                style={{
                  background: "#fff",
                  color: "#555",
                  border: "1.5px solid #ccc",
                  borderRadius: 8,
                  padding: "9px 12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <i
                  className={`bi bi-${scanActive ? "pause-circle" : "play-circle"}`}
                />
              </button>

              <button
                onClick={() =>
                  this.setState({ 
                    showManual: true, 
                    scanActive: false,
                    isEditing: false,
                    editId: null,
                    manualDonorId: "",
                    manualBGroup: "",
                    manualPatientId: "",
                    manualDonorName: "",
                    manualDate: new Date().toISOString().split("T")[0],
                    manualCamp: "",
                  })
                }
                style={{
                  background: "#fff",
                  color: "#c9140e",
                  border: "1.5px solid #c9140e",
                  borderRadius: 8,
                  padding: "9px 16px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <i className="bi bi-pencil-square" /> Manual Entry
              </button>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="alert alert-danger d-flex align-items-center py-2 small mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2" /> {error}
            </div>
          )}
          {success && (
            <div className="alert alert-success d-flex align-items-center py-2 small mb-3">
              <i className="bi bi-check-circle-fill me-2" /> {success}
            </div>
          )}

          {/* Scan feedback card */}
          {(saving || scanned) && (
            <div
              className="card border-0 mb-4 shadow-sm"
              style={{
                borderLeft: "4px solid #c9140e",
                background: saving ? "#fff8f8" : "#f0fff4",
              }}
            >
              <div className="card-body d-flex align-items-center gap-3 py-3">
                {saving ? (
                  <>
                    <div className="spinner-border text-danger spinner-border-sm" />
                    <div>
                      <p className="mb-0 fw-semibold small">
                        {scanned?.mode === "issue"
                          ? "Issuing bag..."
                          : isEditing ? "Updating donation..." : "Saving donation..."}
                      </p>
                      {scanned && (
                        <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                          Donor:{" "}
                          <strong>
                            {this.getDonorById(scanned.donor_id)?.full_name ||
                              scanned.donor_id}
                          </strong>{" "}
                          &nbsp;|&nbsp; Patient ID:{" "}
                          <strong>{scanned.patient_id || "—"}</strong>{" "}
                          &nbsp;|&nbsp; Blood Group:{" "}
                          <strong>{scanned.blood_group}</strong> &nbsp;|&nbsp;
                          Date:{" "}
                          <strong>
                            {this.formatDate(scanned.donation_date)}
                          </strong>
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle-fill text-success fs-4" />
                    <div>
                      <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                        Donor:{" "}
                        <strong>
                          {this.getDonorById(scanned?.donor_id)?.full_name ||
                            scanned?.donor_id}
                        </strong>{" "}
                        &nbsp;|&nbsp; Patient ID:{" "}
                        <strong>{scanned?.patient_id || "—"}</strong>{" "}
                        &nbsp;|&nbsp; Blood Group:{" "}
                        <strong>{scanned?.blood_group}</strong> &nbsp;|&nbsp;
                        Date:{" "}
                        <strong>
                          {this.formatDate(scanned?.donation_date)}
                        </strong>
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Scan instruction banner */}
          {!saving && !scanned && scanActive && (
            <div
              className="card border-0 mb-4"
              style={{
                background:
                  scanMode === "add"
                    ? "linear-gradient(135deg, #198754 0%, #146c43 100%)"
                    : "linear-gradient(135deg, #c9140e 0%, #a00f0b 100%)",
                borderRadius: 12,
              }}
            >
              <div className="card-body text-white d-flex align-items-center gap-4 py-3">
                <i className="bi bi-upc-scan" style={{ fontSize: 40 }} />
                <div>
                  <p className="mb-0 fw-bold">
                    {scanMode === "add"
                      ? "Scanner Ready — Adding Bags"
                      : "Scanner Ready — Issuing Bags"}
                  </p>
                  <p className="mb-0 small" style={{ opacity: 0.85 }}>
                    {scanMode === "add"
                      ? "Scan a new blood bag barcode to record a donation."
                      : "Scan an existing bag's barcode to issue it immediately."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Summary cards */}
          <div className="row g-3 mb-4">
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center py-3">
                  <div
                    style={{ fontSize: 28, fontWeight: 800, color: "#c9140e" }}
                  >
                    {total}
                  </div>
                  <div className="text-muted small">Total Donations</div>
                  {this.state.donations.filter((d) => d.status === "quarantine")
                    .length > 0 && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#dc3545",
                        fontWeight: 600,
                        marginTop: 4,
                        borderTop: "1px solid #f0f0f0",
                        paddingTop: 4,
                      }}
                    >
                      ⚠️ Quarantine:{" "}
                      {
                        this.state.donations.filter(
                          (d) => d.status === "quarantine",
                        ).length
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center py-3">
                  <div
                    style={{ fontSize: 28, fontWeight: 800, color: "#198754" }}
                  >
                    {todayCount}
                  </div>
                  <div className="text-muted small">Today</div>
                </div>
              </div>
            </div>

            {Object.entries(groupCounts)
              .filter(([, c]) => c > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([g, c]) => {
                const quarantineCount = this.state.donations.filter(
                  (d) => d.blood_group === g && d.status === "quarantine",
                ).length;

                return (
                  <div key={g} className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center py-3">
                        <span
                          style={{
                            background: this.bgColor(g),
                            color: "#fff",
                            borderRadius: 6,
                            padding: "2px 10px",
                            fontWeight: 700,
                            fontSize: 18,
                          }}
                        >
                          {g}
                        </span>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 800,
                            marginTop: 4,
                          }}
                        >
                          {c}
                        </div>
                        <div className="text-muted small">donations</div>
                        {quarantineCount > 0 && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#dc3545",
                              fontWeight: 600,
                              marginTop: 4,
                              borderTop: "1px solid #f0f0f0",
                              paddingTop: 4,
                            }}
                          >
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                            {quarantineCount} in quarantine
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Table card */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h6 className="fw-bold mb-0">Donation Records</h6>
                <div className="d-flex gap-2 flex-wrap">
                  <div className="input-group" style={{ width: 220 }}>
                    <span
                      className="input-group-text bg-white border-end-0"
                      style={{ fontSize: 13 }}
                    >
                      <i className="bi bi-search text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search donor or patient ID..."
                      style={{ fontSize: 13 }}
                      value={searchTerm}
                      onChange={(e) =>
                        this.setState({ searchTerm: e.target.value })
                      }
                      onFocus={() => this.setState({ scanActive: false })}
                      onBlur={() =>
                        this.setState({ scanActive: true }, () =>
                          this.focusScanInput(),
                        )
                      }
                    />
                  </div>
                  <select
                    className="form-select"
                    style={{ width: 110, fontSize: 13 }}
                    value={filterBloodGroup}
                    onChange={(e) =>
                      this.setState({ filterBloodGroup: e.target.value })
                    }
                  >
                    <option value="">All Groups</option>
                    {bloodGroups.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-danger" />
                  <p className="text-muted small mt-2">Loading donations...</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <div style={{ fontSize: 48 }}>🩸</div>
                  <p className="mt-2 mb-0 fw-semibold">No donations found.</p>
                  <p className="small">
                    Scan a blood bag barcode or use Manual Entry.
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ fontSize: 13 }}>#</th>
                        <th style={{ fontSize: 13 }}>Donor ID</th>
                        <th style={{ fontSize: 13 }}>Donor Name</th>
                        <th style={{ fontSize: 13 }}>Patient ID</th>
                        <th style={{ fontSize: 13 }}>Blood Group</th>
                        <th style={{ fontSize: 13 }}>Donation Date</th>
                        <th style={{ fontSize: 13 }}>Shelf Life</th>
                        <th style={{ fontSize: 13 }}>Scanned At</th>
                        <th style={{ fontSize: 13 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((d, i) => {
                        const donationDate = new Date(d.donation_date);
                        const today = new Date();
                        const totalDays = Number(d.shelf_life_days) || 35;
                        const usedDays = Math.floor(
                          (today - donationDate) / (1000 * 60 * 60 * 24),
                        );
                        const remainingDays = Math.max(0, totalDays - usedDays);
                        const shelfLifePercentage = Math.round(
                          (remainingDays / totalDays) * 100,
                        );
                        return (
                          <tr key={d.id}>
                            <td className="text-muted small">{i + 1}</td>
                            <td className="fw-semibold small">
                              {d.donor_code || `#${d.donor_id}`}
                            </td>
                            <td>
                              <div className="fw-semibold small">
                                {d.donor_name || "—"}
                              </div>
                              {d.donor_phone && (
                                <div
                                  className="text-muted"
                                  style={{ fontSize: 11 }}
                                >
                                  {d.donor_phone}
                                </div>
                              )}
                            </td>
                            <td className="small">{d.patient_id || "—"}</td>
                            <td>
                              <span
                                style={{
                                  background: this.bgColor(d.blood_group),
                                  color: "#fff",
                                  borderRadius: 6,
                                  padding: "3px 10px",
                                  fontWeight: 700,
                                  fontSize: 13,
                                }}
                              >
                                {d.blood_group}
                              </span>
                            </td>
                            <td className="small">
                              {this.formatDate(d.donation_date)}
                            </td>
                            <td>
                              <ShelfLifeBadge
                                days={remainingDays}
                                percent={shelfLifePercentage}
                                status={d.status}
                              />
                            </td>
                            <td className="small text-muted">
                              {d.created_at
                                ? this.formatDate(d.created_at)
                                : "—"}
                            </td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                {/* Edit Button */}
                                <button
                                  className="btn btn-sm btn-outline-primary py-0 px-2"
                                  onClick={() => this.openEditModal(d)}
                                  title="Edit Donation"
                                  style={{ fontSize: 12 }}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>

                                {/* Delete Button */}
                                <button
                                  className="btn btn-sm btn-outline-danger py-0 px-2"
                                  onClick={() => this.openDeleteConfirm(d)}
                                  title="Delete Donation"
                                  style={{ fontSize: 12 }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>

                                {/* Issue Button */}
                                {d.status !== "quarantine" &&
                                  d.status !== "discarded" &&
                                  d.status !== "issued" && (
                                    <button
                                      className="btn btn-sm py-0 px-2"
                                      style={{
                                        background: "#198754",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: 6,
                                        fontSize: 11,
                                        fontWeight: 600,
                                      }}
                                      onClick={() =>
                                        this.setState({
                                          issueTarget: d,
                                          scanActive: false,
                                        })
                                      }
                                    >
                                      Issue
                                    </button>
                                  )}
                                
                                {/* Discard Button */}
                                {d.status === "quarantine" && (
                                  <button
                                    className="btn btn-sm py-0 px-2"
                                    style={{
                                      background: "#dc3545",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: 6,
                                      fontSize: 11,
                                      fontWeight: 600,
                                    }}
                                    onClick={() =>
                                      this.setState({ discardTarget: d })
                                    }
                                  >
                                    Discard
                                  </button>
                                )}
                                
                                {/* Status Badge */}
                                {d.status === "issued" && (
                                  <span
                                    className="badge bg-secondary"
                                    style={{ fontSize: 10 }}
                                  >
                                    Issued
                                  </span>
                                )}
                                {d.status === "discarded" && (
                                  <span
                                    className="badge bg-danger"
                                    style={{ fontSize: 10 }}
                                  >
                                    Discarded
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {filtered.length > 0 && (
                <div className="mt-2 text-muted small text-end">
                  Showing {filtered.length} of {total} records
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Manual Entry / Edit Modal ── */}
        {showManual && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2000,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => {
              this.setState({ 
                showManual: false, 
                isEditing: false, 
                editId: null,
                scanActive: true 
              }, () => this.focusScanInput());
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                width: "100%",
                maxWidth: 440,
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h6 style={{ margin: 0, fontWeight: 700 }}>
                  <i className={`bi bi-${isEditing ? 'pencil-square' : 'plus-circle'} me-2 text-danger`} />
                  {isEditing ? "Edit Donation" : "Manual Donation Entry"}
                </h6>
                <button
                  onClick={() => {
                    this.setState({ 
                      showManual: false, 
                      isEditing: false, 
                      editId: null,
                      scanActive: true 
                    }, () => this.focusScanInput());
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 20,
                    cursor: "pointer",
                    color: "#888",
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={(e) => this.handleManualSubmit(e)}>
                <div style={{ padding: 20 }} className="d-flex flex-column gap-3">
                  {/* Blood Group */}
                  <div>
                    <label className="form-label fw-semibold small mb-1">
                      Blood Group *
                    </label>
                    <select
                      className="form-select"
                      value={manualBGroup}
                      onChange={(e) =>
                        this.setState({
                          manualBGroup: e.target.value,
                          manualDonorId: "",
                        })
                      }
                     
                    >
                      <option value="">-- Select Blood Group --</option>
                      {bloodGroups.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  
                  </div>

                  {/* Donor */}
                  <div>
                    <label className="form-label fw-semibold small mb-1">
                      Donor *
                    </label>
                   <select
  className="form-select"
  value={manualDonorId}
  onChange={(e) => this.setState({ manualDonorId: e.target.value })}
>
  <option value="">
    {manualBGroup ? "-- Select Donor --" : "-- Select Blood Group First --"}
  </option>
  {this.state.donorsList
    .filter((d) => !manualBGroup || d.blood_group === manualBGroup)
    .filter((d) => (d.status || "").toLowerCase() === "approved")
    .filter(
      (d) =>
        !ineligibleDonorIds.has(String(d.id)) ||
        (isEditing && String(d.donor_id || d.id) === String(manualDonorId)),
    )
    .map((d) => (
      <option key={d.id} value={d.donor_id || d.id}>
        {d.donor_id || d.id} — {d.full_name}
      </option>
    ))}
</select>
                  
                  </div>

              
                  {/* Donation Date */}
                  <div>
                    <label className="form-label fw-semibold small mb-1">
                      Donation Date *
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={manualDate}
                      onChange={(e) =>
                        this.setState({ manualDate: e.target.value })
                      }
                    
                    />
                  
                  </div>

                  {/* Patient ID */}
                  <div>
                    <label className="form-label fw-semibold small mb-1">
                      Patient ID{" "}
                      
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. PT-00123"
                      value={manualPatientId}
                      onChange={(e) =>
                        this.setState({ manualPatientId: e.target.value })
                      }
                    />
                  </div>

                 
                </div>

                <div
                  style={{
                    padding: "12px 20px",
                    borderTop: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      this.setState({ 
                        showManual: false, 
                        isEditing: false, 
                        editId: null,
                        scanActive: true 
                      }, () => this.focusScanInput());
                    }}
                    style={{
                      padding: "8px 18px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      background: "#f8f9fa",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      padding: "8px 20px",
                      borderRadius: 8,
                      border: "none",
                      background: isEditing ? "#c9140e" : "#c9140e",
                      color: "#fff",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {saving ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <>
                        <i className={`bi bi-${isEditing ? 'pencil' : 'save'} me-1`} />
                        {isEditing ? "Update Donation" : "Save Donation"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Delete Confirmation Modal ── */}
        {showDeleteConfirm && deleteTarget && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2000,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => {
              this.setState({ 
                showDeleteConfirm: false, 
                deleteTarget: null,
                scanActive: true 
              }, () => this.focusScanInput());
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                width: "100%",
                maxWidth: 400,
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background:"#dc3545"
                }}
              >
                <h6 style={{ margin: 0, fontWeight: 700,color: "white" }}>
                  <i className="bi bi-exclamation-triangle-fill me-2" />
                  Delete Donation Record?
                </h6>
                <button
                  onClick={() => {
                    this.setState({ 
                      showDeleteConfirm: false, 
                      deleteTarget: null,
                      scanActive: true 
                    }, () => this.focusScanInput());
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 20,
                    cursor: "pointer",
                    color: "#ffffff",
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: 20 }}>
                <p className="mb-2">
                  Are you sure you want to permanently delete this donation record?
                </p>
                <div style={{ 
                  background: "#d8dbdd", 
                  padding: 12, 
                  borderRadius: 8,
                  marginBottom: 12 
                }}>
                  <div className="small">
                    <strong>Donor:</strong> {deleteTarget.donor_name || "—"}<br />
                    <strong>Blood Group:</strong> {deleteTarget.blood_group}<br />
                    <strong>Donation Date:</strong> {this.formatDate(deleteTarget.donation_date)}<br />
                    <strong>Patient ID:</strong> {deleteTarget.patient_id || "—"}
                  </div>
                </div>
               
              </div>

              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                }}
              >
                <button
                  onClick={() => {
                    this.setState({ 
                      showDeleteConfirm: false, 
                      deleteTarget: null,
                      scanActive: true 
                    }, () => this.focusScanInput());
                  }}
                  style={{
                    padding: "8px 18px",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    background: "#f8f9fa",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => this.handleDelete()}
                  disabled={deleting}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 8,
                    border: "none",
                    background: "#dc3545",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {deleting ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <>
                      <i className="bi bi-trash me-1" />
                      Yes, Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Discard confirm modal ── */}
        {this.state.discardTarget && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2000,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => this.setState({ discardTarget: null })}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                width: "100%",
                maxWidth: 400,
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <h6 style={{ margin: 0, fontWeight: 700 }}>
                  <i className="bi bi-exclamation-triangle-fill me-2 text-danger" />
                  Discard This Bag?
                </h6>
              </div>
              <div style={{ padding: 20 }}>
                <p className="mb-1 small">
                  This bag's shelf life has ended and it's in{" "}
                  <strong>Quarantine</strong>.
                </p>
                <p className="mb-0 small text-muted">
                  Donor: <strong>{this.state.discardTarget.donor_name}</strong>{" "}
                  &nbsp;|&nbsp; Blood Group:{" "}
                  <strong>{this.state.discardTarget.blood_group}</strong>{" "}
                  &nbsp;|&nbsp; Donated:{" "}
                  <strong>
                    {this.formatDate(this.state.discardTarget.donation_date)}
                  </strong>
                </p>
                <p className="mt-2 mb-0 small text-danger">
                  Marking it as discarded cannot be undone from this screen.
                </p>
              </div>
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                }}
              >
                <button
                  onClick={() => this.setState({ discardTarget: null })}
                  style={{
                    padding: "8px 18px",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    background: "#f8f9fa",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  No, Keep It
                </button>
                <button
                  onClick={() =>
                    this.handleDiscard(this.state.discardTarget.id)
                  }
                  disabled={this.state.discarding}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 8,
                    border: "none",
                    background: "#c9140e",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {this.state.discarding ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    "Yes, Discard"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Per-row Issue modal ── */}
        {this.state.issueTarget && (
          <IssueModal
            bag={this.state.issueTarget}
            onClose={() =>
              this.setState({ issueTarget: null, scanActive: true }, () =>
                this.focusScanInput(),
              )
            }
            onIssued={(msg) => {
              this.setState({ issueTarget: null, scanActive: true }, () =>
                this.focusScanInput(),
              );
              this.showAlert("success", msg);
              this.fetchDonations();
            }}
          />
        )}
      </div>
    );
  }
}

export default BloodDonations;